import styled from 'styled-components';
import {
  Checkbox,
  DropDown,
  FlexColumn,
  FormControls,
  Radio,
  TextField,
} from '@elementary/common-ui/components';
import { FormControlLabel, RadioGroup } from '@mui/material';
import { conditionalOperators } from 'shared/constants';
import { ConditionalOperator, Severity, SeverityFormField } from 'shared/types';
import FieldRow from './FieldRow';

const STATUSES: Severity[] = ['error', 'warn'];

const ThresholdsWrapper = styled(FlexColumn)`
  gap: 16px;
`;

type Props = {
  value?: SeverityFormField;
  onChange: (val: SeverityFormField) => void;
  allowConditional?: boolean;
  shouldAlignLabels?: boolean;
  allowedConditionalOperators?: ConditionalOperator[];
};

const SeveritySelector = ({
  value,
  onChange,
  allowConditional,
  shouldAlignLabels,
  allowedConditionalOperators,
}: Props) => {
  const operators = allowedConditionalOperators?.length
    ? allowedConditionalOperators
    : conditionalOperators;
  const defaultOperator = operators[0] ?? '>';
  const defaultConditional = { operator: defaultOperator, value: 0 };
  const isConditional = value?.conditionalError || value?.conditionalWarning;

  const handleOperatorChange = (type: Severity, val: ConditionalOperator) => {
    if (type === 'error') {
      onChange?.({
        ...value,
        conditionalError: { ...value?.conditionalError, operator: val },
      });
    } else {
      onChange?.({
        ...value,
        conditionalWarning: { ...value?.conditionalWarning, operator: val },
      });
    }
  };

  const handleThresholdChange = (type: Severity, val: number) => {
    if (type === 'warn') {
      onChange({
        ...value,
        conditionalWarning: {
          ...value?.conditionalWarning,
          value: val,
        },
      });
    } else {
      onChange({
        ...value,
        conditionalError: {
          ...value?.conditionalError,
          value: val,
        },
      });
    }
  };

  const handleCheckboxChange = (type: Severity, val: boolean) => {
    if (type === 'warn') {
      onChange({
        ...value,
        conditionalWarning: val ? defaultConditional : undefined,
      });
    } else {
      onChange({
        ...value,
        conditionalError: val ? defaultConditional : undefined,
      });
    }
  };

  if (!allowConditional) {
    if (shouldAlignLabels) {
      return (
        <FieldRow label="Severity:">
          <DropDown
            sx={{ flex: 1, minWidth: 0, width: '100%' }}
            size="large"
            options={[
              { value: 'error', label: 'Error' },
              { value: 'warn', label: 'Warning' },
            ]}
            value={value?.type}
            onChange={(val) => onChange({ type: val })}
            sortOptions={false}
          />
        </FieldRow>
      );
    }

    return (
      <FormControls.Row>
        <FormControls.FormText>Severity:</FormControls.FormText>
        <DropDown
          size="large"
          options={[
            { value: 'error', label: 'Error' },
            { value: 'warn', label: 'Warning' },
          ]}
          value={value?.type}
          onChange={(val) => onChange({ type: val })}
          sortOptions={false}
        />
      </FormControls.Row>
    );
  }

  return (
    <>
      <FormControls.FormTitle>Severity</FormControls.FormTitle>
      <FormControls.NestedFields>
        <RadioGroup
          sx={{ gap: 1 }}
          value={isConditional ? 'conditional' : 'simple'}
        >
          <FormControlLabel
            sx={{ gap: 1 }}
            value="simple"
            control={<Radio size="small" />}
            onChange={() => onChange({ type: 'error' })}
            label={
              <FormControls.Row>
                <FormControls.FormText>
                  Use simple severity:
                </FormControls.FormText>
                <DropDown
                  size="large"
                  options={[
                    { value: 'error', label: 'Error' },
                    { value: 'warn', label: 'Warning' },
                  ]}
                  value={value?.type}
                  onChange={(val) => onChange({ type: val })}
                  sortOptions={false}
                />
              </FormControls.Row>
            }
          />

          <FormControlLabel
            sx={{ gap: 1, alignItems: 'flex-start' }}
            value="conditional"
            control={<Radio size="small" />}
            onChange={() =>
              onChange({
                type: 'error',
                conditionalError: defaultConditional,
              })
            }
            label={
              <FlexColumn sx={{ gap: 2 }}>
                <FormControls.FormText sx={{ mt: 1 }}>
                  Use conditional severity:
                </FormControls.FormText>

                <ThresholdsWrapper>
                  {STATUSES.map((status) => (
                    <FormControls.Row key={status} sx={{ maxWidth: '620px' }}>
                      <FormControls.Row>
                        <Checkbox
                          $noPadding
                          checked={
                            status === 'error'
                              ? !!value?.conditionalError
                              : !!value?.conditionalWarning
                          }
                          onChange={(_, val) =>
                            handleCheckboxChange(status, val)
                          }
                        />

                        <FormControls.FormText>
                          Return {status} if the number of failures
                        </FormControls.FormText>
                      </FormControls.Row>

                      <FormControls.Row>
                        {operators.length === 1 ? (
                          <FormControls.FormText>
                            {operators[0]}
                          </FormControls.FormText>
                        ) : (
                          <DropDown
                            sx={{ width: '64px', minWidth: '64px' }}
                            size="large"
                            options={operators.map((val) => ({
                              value: val,
                              label: val,
                            }))}
                            value={
                              status === 'error'
                                ? value?.conditionalError?.operator
                                : value?.conditionalWarning?.operator
                            }
                            onChange={(val) =>
                              handleOperatorChange(status, val)
                            }
                            sortOptions={false}
                            placeholder={defaultOperator}
                            disabled={
                              !allowConditional ||
                              (status === 'error'
                                ? !value?.conditionalError
                                : !value?.conditionalWarning)
                            }
                          />
                        )}

                        <TextField
                          sx={{ width: '64px' }}
                          value={
                            status === 'error'
                              ? value?.conditionalError?.value ?? ''
                              : value?.conditionalWarning?.value ?? ''
                          }
                          onChange={(e) =>
                            handleThresholdChange(
                              status,
                              Number(e.target.value),
                            )
                          }
                          numeric
                          placeholder="0"
                          disabled={
                            !allowConditional ||
                            (status === 'error'
                              ? !value?.conditionalError
                              : !value?.conditionalWarning)
                          }
                        />
                      </FormControls.Row>
                    </FormControls.Row>
                  ))}
                </ThresholdsWrapper>
              </FlexColumn>
            }
          />
        </RadioGroup>
      </FormControls.NestedFields>
    </>
  );
};

export default SeveritySelector;
